/**
 * SMPTE timecode core — frames-first math aligned with eoyilmaz/timecode
 * (https://github.com/eoyilmaz/timecode), exposed with 0-based frameCount
 * to match smpte-timecode / existing cc-lib callers.
 *
 * Intentional divergences from Python:
 * - frameCount is 0-based (Python.frames is 1-based; frame_number matches us)
 * - seconds use exact rationals (frameCount * den/num), not Python float/to_realtime
 * - toString() uses ':' by default for API parity with secToTc (which strips ';')
 */

function parseFloatRate(frameRate) {
  return parseFloat(frameRate);
}
function rationalFrameRate(frameRate) {
  frameRate = parseFloatRate(frameRate);
  if (frameRate === 23.976 || frameRate === 23.98) {
    return [24000, 1001];
  } else if (frameRate === 29.97) {
    return [30000, 1001];
  } else if (frameRate === 59.94) {
    return [60000, 1001];
  }
  return [frameRate, 1];
}
function isNtscRate(fps) {
  const intFps = Math.round(fps * 1001 / 1000);
  const expectedNtsc = intFps * 1000 / 1001;
  return Math.abs(fps - expectedNtsc) < 0.005;
}
function intFramerate(frameRate) {
  const fps = parseFloatRate(frameRate);
  if (isNtscRate(fps)) {
    return Math.round(fps * 1001 / 1000);
  }
  return Math.round(fps);
}
function resolveDropFrame(frameRate, dropFrame) {
  const fps = parseFloatRate(frameRate);
  // DF only valid at 29.97 / 59.94 (and NTSC multiples of 30)
  if (fps !== 29.97 && fps !== 59.94) {
    return false;
  }
  return !!dropFrame;
}
function dropFramesForRate(frameRate) {
  const fps = parseFloatRate(frameRate);
  // Nearest integer to 6% of framerate (Heidelberger / eoyilmaz)
  return Math.round(fps * 0.066666);
}
function pad2(n) {
  return String(n).padStart(2, "0");
}
function parseTimecodeParts(timecode) {
  if (typeof timecode === "number") {
    const hexRepr = "0x" + Math.trunc(timecode).toString(16).padStart(8, "0");
    return [parseInt(hexRepr.slice(2, 4), 16), parseInt(hexRepr.slice(4, 6), 16), parseInt(hexRepr.slice(6, 8), 16), parseInt(hexRepr.slice(8, 10), 16)];
  }
  const parts = String(timecode).replace(/;|\./g, ":").split(":");
  return [parseInt(parts[0], 10) || 0, parseInt(parts[1], 10) || 0, parseInt(parts[2], 10) || 0, parseInt(parts[3], 10) || 0];
}

/**
 * Convert HH:MM:SS:FF (or ;) to 0-based frame count.
 */
function tcToFrameCount(timecode, frameRate, dropFrame) {
  const df = resolveDropFrame(frameRate, dropFrame);
  const [hours, minutes, seconds, frames] = parseTimecodeParts(timecode);
  const ifps = intFramerate(frameRate);
  const drop_frames = df ? dropFramesForRate(frameRate) : 0;
  const hour_frames = ifps * 60 * 60;
  const minute_frames = ifps * 60;
  const total_minutes = 60 * hours + minutes;
  const frame_number = hour_frames * hours + minute_frames * minutes + ifps * seconds + frames - drop_frames * (total_minutes - Math.floor(total_minutes / 10));
  return frame_number; // 0-based (Python returns frame_number+1 as frames)
}

/**
 * Convert 0-based frame count to [hrs, mins, secs, frs].
 */
function frameCountToParts(frameCount, frameRate, dropFrame, skipRollover = true) {
  const df = resolveDropFrame(frameRate, dropFrame);
  const fps = parseFloatRate(frameRate);
  const ifps = intFramerate(frameRate);
  const drop_frames = df ? dropFramesForRate(frameRate) : 0;
  const frames_per_10_minutes = Math.round(fps * 60 * 10);
  const frames_per_24_hours = Math.round(fps * 60 * 60 * 24);
  const frames_per_minute = Math.round(fps) * 60 - drop_frames;
  let frame_number = Math.max(0, Math.round(frameCount));
  if (!skipRollover) {
    frame_number %= frames_per_24_hours;
  }
  if (df) {
    const d = Math.floor(frame_number / frames_per_10_minutes);
    const m = frame_number % frames_per_10_minutes;
    if (m > drop_frames) {
      frame_number += drop_frames * 9 * d + drop_frames * Math.floor((m - drop_frames) / frames_per_minute);
    } else {
      frame_number += drop_frames * 9 * d;
    }
  }
  const frs = frame_number % ifps;
  const secs = Math.floor(frame_number / ifps) % 60;
  const mins = Math.floor(Math.floor(frame_number / ifps) / 60) % 60;
  const hrs = Math.floor(Math.floor(frame_number / ifps) / 60 / 60);
  return [hrs, mins, secs, frs];
}
function frameCountToTcString(frameCount, frameRate, dropFrame, useDropSeparator = false) {
  const df = resolveDropFrame(frameRate, dropFrame);
  const [hrs, mins, secs, frs] = frameCountToParts(frameCount, frameRate, dropFrame, true);
  const sep = useDropSeparator && df ? ";" : ":";
  return `${pad2(hrs)}:${pad2(mins)}:${pad2(secs)}${sep}${pad2(frs)}`;
}
function frameCountToSeconds(frameCount, frameRate) {
  const [num, den] = rationalFrameRate(frameRate);
  return frameCount * den / num;
}
function secondsToFrameCount(seconds, frameRate) {
  const [num, den] = rationalFrameRate(frameRate);
  let frameCount = Math.max(0, Math.round(parseFloat(seconds) * num / den));
  if (!isFinite(frameCount)) {
    frameCount = 0;
  }
  return frameCount;
}

/**
 * Mutable timecode object compatible with smpte-timecode usage in cc-lib
 * (.frameCount, .add, .subtract, .toString).
 */
class Timecode {
  constructor(timecodeOrFrames, frameRate, dropFrame = false) {
    this.frameRate = parseFloatRate(frameRate);
    this.dropFrame = resolveDropFrame(this.frameRate, dropFrame);
    this._frameCount = 0;
    if (typeof timecodeOrFrames === "number") {
      this._frameCount = Math.max(0, Math.round(timecodeOrFrames));
    } else if (timecodeOrFrames != null) {
      this._frameCount = tcToFrameCount(timecodeOrFrames, this.frameRate, this.dropFrame);
    }
  }
  get frameCount() {
    return this._frameCount;
  }
  set frameCount(value) {
    this._frameCount = Math.max(0, Math.round(value));
  }
  toString(useDropSeparator = true) {
    return frameCountToTcString(this._frameCount, this.frameRate, this.dropFrame, useDropSeparator);
  }

  /**
   * @param {number|string|Timecode} value frames, timecode string, or Timecode
   */
  add(value) {
    this._frameCount += this._coerceFrames(value);
    if (this._frameCount < 0) {
      this._frameCount = 0;
    }
    return this;
  }
  subtract(value) {
    this._frameCount -= this._coerceFrames(value);
    if (this._frameCount < 0) {
      this._frameCount = 0;
    }
    return this;
  }
  _coerceFrames(value) {
    if (typeof value === "number") {
      return Math.round(value);
    }
    if (value && typeof value.frameCount === "number") {
      return value.frameCount;
    }
    // Timecode string (possibly DF) at this instance's rate/DF
    return tcToFrameCount(String(value), this.frameRate, this.dropFrame);
  }
}

/**
 * Factory matching smpte-timecode(call) style: Timecode(tc|frames, rate, df)
 */
function create(timecodeOrFrames, frameRate, dropFrame = false) {
  // Support rational tuple like smpte-timecode([num, den])
  let rate = frameRate;
  if (Array.isArray(frameRate)) {
    const [num, den] = frameRate;
    rate = num / den;
    // Normalize known rationals to canonical float labels
    if (num === 24000 && den === 1001) rate = 23.976;else if (num === 30000 && den === 1001) rate = 29.97;else if (num === 60000 && den === 1001) rate = 59.94;
  }
  return new Timecode(timecodeOrFrames, rate, dropFrame);
}
export default {
  rationalFrameRate,
  intFramerate,
  resolveDropFrame,
  tcToFrameCount,
  frameCountToParts,
  frameCountToTcString,
  frameCountToSeconds,
  secondsToFrameCount,
  Timecode,
  create
};